import { useState, useCallback, useEffect } from 'react';
import { DateRangeMode, getModeMeta } from '../components/validation/dateRangeModesConfig';
import utilSvc from '../../core/utilSvc';
import { formatLocalDate } from '../components/validation/ValidInputDate.utils';
import { computeEndChangeEffect } from '../utils/date/computeEndChangeEffect';
import { computeStartChangeEffect } from '../utils/date/computeStartChangeEffect';

// Utility: Get initial start/end, month/year, qtr/qtrYear for a given mode
export function getInitialDateRangeForMode(
    mode: DateRangeMode,
    today: Date = new Date(),
): {
    start: Date;
    end: Date;
    monthlyMonth: number;
    monthlyYear: number;
    qtr: number;
    qtrYear: number;
} {
    const meta = getModeMeta(mode);
    if (!meta || !meta.getRange) {
        return {
            start: utilSvc.dt.zeroTime(today),
            end: utilSvc.dt.zeroTime(today),
            monthlyMonth: today.getMonth() + 1,
            monthlyYear: today.getFullYear(),
            qtr: Math.floor(today.getMonth() / 3) + 1,
            qtrYear: today.getFullYear(),
        };
    }
    if (mode === 'MONTHLY') {
        const { start, end } = meta.getRange({ monthlyMonth: today.getMonth() + 1, monthlyYear: today.getFullYear() });
        return {
            start,
            end,
            monthlyMonth: today.getMonth() + 1,
            monthlyYear: today.getFullYear(),
            qtr: Math.floor(today.getMonth() / 3) + 1,
            qtrYear: today.getFullYear(),
        };
    } else if (mode === 'QTR') {
        // TODO: Figure out we if need to account for unset/invalid qtr or qtrYear

        // Always use the most recent fully completed quarter
        let qtr = Math.floor(today.getMonth() / 3); // 0-based: 0=Q1, 1=Q2, 2=Q3, 3=Q4
        let qtrYear = today.getFullYear();
        if (qtr === 0) {
            qtr = 4;
            qtrYear = today.getFullYear() - 1;
        }
        const { start, end } = meta.getRange({ qtr, qtrYear });
        return {
            start,
            end,
            monthlyMonth: today.getMonth() + 1,
            monthlyYear: today.getFullYear(),
            qtr,
            qtrYear,
        };
    } else {
        const { start, end } = meta.getRange({ today: utilSvc.dt.zeroTime(today) });
        return {
            start,
            end,
            monthlyMonth: today.getMonth() + 1,
            monthlyYear: today.getFullYear(),
            qtr: Math.floor(today.getMonth() / 3) + 1,
            qtrYear: today.getFullYear(),
        };
    }
}

type UseDateRangeControllerStateReturn = {
    monthlyMonth: number;
    monthlyYear: number;
    qtr: number;
    qtrYear: number;
    handleModeChange: (newMode: string) => void;
    handleStartChange: (date: Date) => void;
    handleEndChange: (date: Date) => void;
    handleMonthlyChange: (month: number, year: number) => void;
    handleQuarterChange: (quarter: number, year: number) => void;
};

// UI-only state for DateRangeController
export function useDateRangeControllerState(
    dateRangeMode: string,
    dateRangeStart: Date,
    dateRangeEnd: Date,
    setProperty: (key: any, val: any) => void,
    setMultipleProperties?: (updates: Record<string, any>) => void,
): UseDateRangeControllerStateReturn & { startError: string; endError: string } {
    const today = new Date();
    const [monthlyMonth, setMonthlyMonth] = useState(dateRangeStart.getMonth() + 1);
    const [monthlyYear, setMonthlyYear] = useState(dateRangeStart.getFullYear());
    // Calculate the most recent fully completed quarter for QTR mode
    let initialQtr: number;
    let initialQtrYear: number;
    if (dateRangeMode === 'QTR') {
        // Last fully completed quarter logic
        const m = today.getMonth();
        if (m <= 2) {
            // Jan, Feb, Mar
            initialQtr = 4;
            initialQtrYear = today.getFullYear() - 1;
        } else if (m <= 5) {
            // Apr, May, Jun
            initialQtr = 1;
            initialQtrYear = today.getFullYear();
        } else if (m <= 8) {
            // Jul, Aug, Sep
            initialQtr = 2;
            initialQtrYear = today.getFullYear();
        } else {
            // Oct, Nov, Dec
            initialQtr = 3;
            initialQtrYear = today.getFullYear();
        }
    } else {
        initialQtr = Math.floor(dateRangeStart.getMonth() / 3) + 1;
        initialQtrYear = dateRangeStart.getFullYear();
    }
    const [qtr, setQtr] = useState(() => (dateRangeMode === 'QTR' ? initialQtr : initialQtr));
    const [qtrYear, setQtrYear] = useState(() => (dateRangeMode === 'QTR' ? initialQtrYear : initialQtrYear));
    // Local error state for cross-field validation
    const [startError, setStartError] = useState('');
    const [endError, setEndError] = useState('');

    // Batch-write helper: uses setMultipleProperties (single atom write) when available,
    // falling back to sequential setProperty calls.
    const batchSet = useCallback(
        (updates: Record<string, any>) => {
            if (setMultipleProperties) {
                setMultipleProperties(updates);
            } else {
                for (const [key, val] of Object.entries(updates)) {
                    setProperty(key, val);
                }
            }
        },
        [setMultipleProperties, setProperty],
    );

    // Reset when mode changes
    // Only set month/year to today when mode switches to MONTHLY
    useEffect(() => {
        if (dateRangeMode === 'MONTHLY') {
            // Only reset to current month/year if not already set in state
            if (!monthlyMonth || !monthlyYear) {
                const today = new Date();
                const currentMonth = today.getMonth() + 1;
                const currentYear = today.getFullYear();
                setMonthlyMonth(currentMonth);
                setMonthlyYear(currentYear);
                const meta = getInitialDateRangeForMode('MONTHLY', today);
                batchSet({ StartDate: formatLocalDate(meta.start), EndDate: formatLocalDate(meta.end) });
            }
        } else if (dateRangeMode === 'QTR') {
            // Set to last fully completed quarter
            const m = today.getMonth();
            let qtr: number;
            let qtrYear: number;
            if (m <= 2) {
                // Jan, Feb, Mar
                qtr = 4;
                qtrYear = today.getFullYear() - 1;
            } else if (m <= 5) {
                // Apr, May, Jun
                qtr = 1;
                qtrYear = today.getFullYear();
            } else if (m <= 8) {
                // Jul, Aug, Sep
                qtr = 2;
                qtrYear = today.getFullYear();
            } else {
                // Oct, Nov, Dec
                qtr = 3;
                qtrYear = today.getFullYear();
            }
            setQtr(qtr);
            setQtrYear(qtrYear);
            const meta = getModeMeta('QTR');
            const { start, end } = meta.getRange({ qtr, qtrYear });
            batchSet({ StartDate: formatLocalDate(start), EndDate: formatLocalDate(end) });
        }
        // Clamp initial range for CUSTOM3/CUSTOM92 to maxDays, ending at today if needed
        if (dateRangeMode === 'CUSTOM3' || dateRangeMode === 'CUSTOM92') {
            const today = utilSvc.dt.zeroTime(new Date());
            const end = dateRangeEnd > today ? today : utilSvc.dt.zeroTime(dateRangeEnd);
            const start = utilSvc.dt.zeroTime(dateRangeStart);

            const startStr = formatLocalDate(start);
            const endStr = formatLocalDate(end);

            const clampUpdates: Record<string, string> = {};
            if (startStr !== formatLocalDate(dateRangeStart)) clampUpdates.StartDate = startStr;
            if (endStr !== formatLocalDate(dateRangeEnd)) clampUpdates.EndDate = endStr;
            if (Object.keys(clampUpdates).length > 0) batchSet(clampUpdates);
        }
        /* eslint-disable-next-line */
    }, [dateRangeMode]);

    useEffect(() => {
        // Compare zeroTime versions for date-only comparison
        const start = utilSvc.dt.zeroTime(dateRangeStart);
        const end = utilSvc.dt.zeroTime(dateRangeEnd);
        const today = utilSvc.dt.zeroTime(new Date());
        const isStartAfterEnd = start > end;
        const isStartInFuture = start > today;
        const isEndInFuture = end > today;

        if (!isStartAfterEnd && !isStartInFuture && !isEndInFuture) {
            setStartError('');
            setEndError('');
        }
    }, [dateRangeStart, dateRangeEnd]);

    // Sync monthlyMonth/monthlyYear with dateRangeStart when mode is MONTHLY and values differ
    useEffect(() => {
        if (dateRangeMode === 'MONTHLY') {
            const newMonth = dateRangeStart.getMonth() + 1;
            const newYear = dateRangeStart.getFullYear();
            if (monthlyMonth !== newMonth) {
                setMonthlyMonth(newMonth);
            }
            if (monthlyYear !== newYear) {
                setMonthlyYear(newYear);
            }
        }
    }, [dateRangeMode, dateRangeStart, monthlyMonth, monthlyYear]);

    // Handler for mode change
    const handleModeChange = useCallback(
        (newMode: string) => {
            const initial = getInitialDateRangeForMode(newMode as DateRangeMode, new Date());
            batchSet({
                DateRangeMode: newMode,
                StartDate: formatLocalDate(initial.start),
                EndDate: formatLocalDate(initial.end),
            });
            if (newMode === 'MONTHLY') {
                setMonthlyMonth(initial.monthlyMonth);
                setMonthlyYear(initial.monthlyYear);
            } else if (newMode === 'QTR') {
                setQtr(initial.qtr);
                setQtrYear(initial.qtrYear);
            }
        },
        [batchSet],
    );

    // Handler for start date change
    const handleStartChange = useCallback(
        (date: Date) => {
            const s = utilSvc.dt.zeroTime(date);
            const e = utilSvc.dt.zeroTime(dateRangeEnd);

            if (dateRangeMode === 'CUSTOM3' || dateRangeMode === 'CUSTOM92') {
                const meta = getModeMeta(dateRangeMode as DateRangeMode);
                const maxDays = meta?.maxDays ?? 1;
                const today = utilSvc.dt.zeroTime(new Date());

                const effect = computeStartChangeEffect(s, e, today, maxDays, startError, endError, dateRangeEnd);

                if (effect.earlyReturn) {
                    batchSet(effect.batchWrite!);
                    setStartError(effect.startError!);
                    return;
                }
                // Apply in original order: startError → endError → extra EndDate write → final batchSet
                if (effect.startError !== null) setStartError(effect.startError);
                if (effect.endError !== null) setEndError(effect.endError);
                if (effect.extraEndDateWrite !== null) setProperty('EndDate', effect.extraEndDateWrite);
                batchSet({ StartDate: effect.finalStartDate!, EndDate: effect.finalEndDate! });
            } else {
                setProperty('StartDate', formatLocalDate(date));
            }
        },
        [dateRangeMode, dateRangeEnd, setProperty, batchSet, startError, endError],
    );

    // Handler for end date change
    const handleEndChange = useCallback(
        (date: Date) => {
            const s = utilSvc.dt.zeroTime(dateRangeStart);
            const e = utilSvc.dt.zeroTime(date);

            if (dateRangeMode === 'CUSTOM3' || dateRangeMode === 'CUSTOM92') {
                const meta = getModeMeta(dateRangeMode as DateRangeMode);
                const maxDays = meta?.maxDays ?? 1;
                const today = utilSvc.dt.zeroTime(new Date());

                const effect = computeEndChangeEffect(s, e, today, maxDays, endError, startError);

                if (effect.earlyReturn) {
                    batchSet(effect.batchWrite!);
                    setEndError(effect.endError!);
                    return;
                }
                // Apply in original order: StartDate write → endError → startError → EndDate write
                if (effect.startDateWrite !== null) setProperty('StartDate', effect.startDateWrite);
                if (effect.endError !== null) setEndError(effect.endError);
                if (effect.startError !== null) setStartError(effect.startError);
                setProperty('EndDate', effect.endDateWrite!);
            } else {
                setProperty('EndDate', formatLocalDate(date));
            }
        },
        [dateRangeMode, dateRangeStart, setProperty, batchSet, endError, startError],
    );

    // Handler for monthly change
    const handleMonthlyChange = useCallback(
        (month: number, year: number) => {
            const today = new Date();
            const clampedMonth = month;
            let clampedYear = year;
            // If selected month/year is in the future, clamp year to current or previous, but always allow selected month
            const isFutureYear = year > today.getFullYear();
            const isFutureMonth = year === today.getFullYear() && month > today.getMonth() + 1;
            if (isFutureYear) {
                clampedYear = today.getFullYear();
            }
            if (isFutureMonth) {
                clampedYear = today.getFullYear() - 1;
            }
            setMonthlyMonth(clampedMonth);
            setMonthlyYear(clampedYear);

            const meta = getModeMeta('MONTHLY');
            const { start, end } = meta.getRange({ monthlyMonth: clampedMonth, monthlyYear: clampedYear });
            const startStr = formatLocalDate(start);
            const endStr = formatLocalDate(end);

            batchSet({ StartDate: startStr, EndDate: endStr });
        },
        [setMonthlyMonth, setMonthlyYear, batchSet],
    );

    // Handler for quarter change
    const handleQuarterChange = useCallback(
        (quarter: number, year: number) => {
            const today = new Date();
            const currentYear = today.getFullYear();
            // Calculate the end date of the selected quarter/year
            const selectedEnd = new Date(year, quarter * 3, 0, 23, 59, 59, 999); // last day of the quarter
            // If the selected quarter/year SPANS into the future
            if (selectedEnd > today) {
                // If the year is being changed (user is trying to set a future year), prevent the year update
                if (year !== qtrYear) {
                    console.log('[QTR] Preventing year change to future (quarter spans future):', {
                        attemptedQuarter: quarter,
                        attemptedYear: year,
                        currentQuarter: qtr,
                        currentYear: qtrYear,
                    });
                    return;
                }
                // If the quarter is being changed and the span is in the future, set year to previous year
                console.log('[QTR] Selected quarter/year spans into the future, setting year to previous:', {
                    attemptedQuarter: quarter,
                    attemptedYear: year,
                });
                year = currentYear - 1;
            }
            setQtr(quarter);
            setQtrYear(year);
            const meta = getModeMeta('QTR');
            const { start, end } = meta.getRange({ qtr: quarter, qtrYear: year });
            batchSet({ StartDate: formatLocalDate(start), EndDate: formatLocalDate(end) });
        },
        [setQtr, setQtrYear, batchSet, qtrYear, qtr],
    );

    return {
        monthlyMonth,
        monthlyYear,
        qtr,
        qtrYear,
        handleModeChange,
        handleStartChange,
        handleEndChange,
        handleMonthlyChange,
        handleQuarterChange,
        startError,
        endError,
    };
}
